import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "sonner";

/**
 * Cross-origin login-loop guard.
 *
 * Failure mode this exists to prevent: a build is shipped with
 *   REACT_APP_BACKEND_URL=https://api.example.com
 * while the SPA is actually served from https://app.example.com. The
 * browser loads the SPA from one origin and issues XHRs to another, so
 * the session cookie set by the cross-origin login response is treated
 * as "third-party" by Safari ITP / iOS Safari / strict Chrome settings.
 * The cookie is silently dropped on the next request → backend returns
 * 401 → frontend redirects to /login → user logs in → loop.
 *
 * Defense: at runtime, if the configured BACKEND_URL points to a
 * different origin than the page that loaded us, ignore it and use
 * relative paths instead. Same-origin XHRs always get the cookie.
 * This makes the build resilient to a misconfigured deployment env var
 * — no redeploy is needed to recover once the operator fixes it.
 *
 * In dev / preview the configured URL IS the page origin so this
 * branch is a no-op there. The override only kicks in for the
 * deployed-with-wrong-env-var case.
 */
const configuredBase = process.env.REACT_APP_BACKEND_URL || "";
let baseURL = configuredBase;
if (typeof window !== "undefined" && configuredBase) {
  try {
    const cfgHost = new URL(configuredBase).host;
    if (cfgHost && cfgHost !== window.location.host) {
      // Different origin — use relative URLs to stay same-origin.
      baseURL = "";
      console.info(
        `[api] BACKEND_URL host (${cfgHost}) ≠ page host (${window.location.host}); ` +
          "falling back to same-origin to avoid third-party cookie issues.",
      );
    }
  } catch {
    /* malformed URL — fall through with the configured value */
  }
}

/**
 * The origin-safe base URL for direct browser navigations / iframe `src`
 * attributes (not axios XHRs). Same value axios uses: empty string when the
 * configured BACKEND_URL is a different origin than the page (so the request
 * stays first-party and the `app_session` cookie is actually sent), otherwise
 * the configured base. Cookie-authenticated GET endpoints embedded in an
 * <iframe src> (e.g. the funnel node live-thumbnail /preview render) MUST use
 * this — a cross-origin src is a third-party request that Safari ITP / strict
 * Chrome strip the session cookie from, so the endpoint 401s.
 */
export const backendOrigin = baseURL;

export const api = axios.create({
  baseURL,
  withCredentials: true,
});

const SAFE = new Set(["get", "head", "options"]);

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!SAFE.has(method)) {
    const csrf = Cookies.get("csrf_token");
    if (csrf) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
});

// Endpoints that legitimately return 401 — they handle the response
// themselves and we must NOT redirect or our login page would loop.
const AUTH_ENDPOINTS = /\/api\/auth\/(login|me|logout)/;

// Z.127.4 — coalesce noisy rate-limit / credits-exhausted toasts so a
// burst of failed scrapes can't spam the user. One toast per ~10s.
let _lastRlToastAt = 0;
let _lastCreditsToastAt = 0;
function _maybeToast(status, detail, retryHeader) {
  const now = Date.now();
  const det = (detail && typeof detail === "object") ? detail : {};
  const code = det.code || "";
  if (status === 429 || code === "scrapecreators_rate_limited" || det.error === "rate_limited") {
    if (now - _lastRlToastAt < 10_000) return;
    _lastRlToastAt = now;
    const secs = det.retry_after_sec || (retryHeader ? Number(retryHeader) : null) || 60;
    toast.warning(
      det.message || `ScrapeCreators rate limit hit. Retry in ${secs}s.`,
      { duration: 6000, id: "sc-rate-limit", description: "Some results may be empty until the limit resets." },
    );
  } else if (status === 402 || code === "scrapecreators_credits_exhausted" || det.error === "credits_exhausted") {
    if (now - _lastCreditsToastAt < 30_000) return;
    _lastCreditsToastAt = now;
    toast.error(
      det.message || "ScrapeCreators credits exhausted.",
      { duration: 8000, id: "sc-credits", description: "Top up your account to continue scraping." },
    );
  }
}

api.interceptors.response.use(
  (response) => {
    // Surface graceful-failure shape on 2xx (e.g. /youtube/suggest returns
    // {error: "rate_limited"} with HTTP 200).
    const body = response?.data;
    if (body && typeof body === "object" && (body.error === "rate_limited" || body.error === "credits_exhausted")) {
      _maybeToast(null, body, null);
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // If the session expired on a protected page, bounce to /login so the
    // user re-authenticates instead of seeing a sea of 401 toasts/overlays.
    if (
      status === 401 &&
      !AUTH_ENDPOINTS.test(url) &&
      // Opt-out flag: callers that gracefully handle their own 401 (e.g. the
      // preview-link probe, which falls back to the CRM preview URL) set this
      // so a transient 401 doesn't yank the operator out of the editor.
      !error?.config?.skipAuthRedirect &&
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/app/")
    ) {
      window.location.href = `/login?from=${encodeURIComponent(window.location.pathname)}`;
    }
    if (status === 429 || status === 402) {
      _maybeToast(status, error?.response?.data?.detail, error?.response?.headers?.["retry-after"]);
    }
    // FastAPI 422 validation errors put an ARRAY of {type, loc, msg, input,
    // url} objects in `detail`. Dozens of callsites do
    // `toast.error(err?.response?.data?.detail || "...")`; rendering that
    // array crashes React ("Objects are not valid as a React child").
    // Normalize the array shape to a readable string once, here, for every
    // consumer of this client. Non-array object details are left intact —
    // some callers read `detail.code`.
    if (Array.isArray(error?.response?.data?.detail)) {
      error.response.data.detail = apiErrorMessage(error);
    }
    return Promise.reject(error);
  }
);

/**
 * Normalize an axios/FastAPI error into a human-readable string, ALWAYS.
 *
 * FastAPI validation failures (422) return `detail` as an ARRAY of
 * `{type, loc, msg, input, ctx, url}` objects. Passing that array (or any
 * object) straight into `toast.error(...)` / JSX crashes React with
 * "Objects are not valid as a React child". Use this helper anywhere an
 * error message is rendered.
 */
export function apiErrorMessage(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic error list → "field: message" lines.
    const parts = detail
      .map((e) => {
        if (typeof e === "string") return e;
        const loc = Array.isArray(e?.loc)
          ? e.loc.filter((p) => p !== "body").join(".")
          : "";
        const msg = e?.msg || "invalid value";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.error || fallback;
  }
  return err?.response?.data?.message || err?.message || fallback;
}

export default api;
